import { compactCurrencyNumber, formatInKoreaPhoneNumber } from '../shared/user_methods'
import { asArray } from '../shared/functions'
import { dispatchElement } from '../shared/domHelper'

/**
 * el, ctx 정보를 이용해서 v-model reactive data와 연결 여부를 추론:
 */
function getValueOfPetiteNode(ctx) {
  // v-model="expr"이면 ctx.exp이 expr
  if (ctx.exp) {
    try {
      return String(ctx.get());
    } catch {
      // exp가 잘못되었을 때 fallback
    }
  }
  // v-model이 아닐 경우 value 속성 fallback
  return ctx.el.value;
}

function setValueOfPetiteNode(ctx, value) {
  // v-model="expr" 구조가 연결되어 있으면 reactive data에 할당
  if (ctx.exp) {
    try {
      // ctx.set은 없음! => ctx.get(`expr = value`)처럼 간접 할당, 또는 window.proxy 접근
      // safest 방법: scope 내 변수 직접 set 가능 (with block 이용)
      // 아래는 window.proxy 방식 (petite-vue v-scope에서 window에 객체 두는 패턴)
      // ctx.get은 값만, setter 성격이 아님!
      if (ctx.scope && ctx.exp in ctx.scope) {
        ctx.scope[ctx.exp] = value;
        return;
      }
      // vueApp 인스턴스 상에 exp이 루트이면
      if (ctx.ctx && ctx.exp in ctx.ctx) {
        ctx.ctx[ctx.exp] = value;
        return;
      }
      // fallback: 수동 직접 실행 (doesn't always work but covers many)
      Function('scope', `scope["${ctx.exp}"] = arguments[1]`).call(ctx.el, ctx.scope || {}, value);
    } catch {
      // eslint-disable-next-line no-console
      // console.warn('petite-vue setValueOfPetiteNode fallback on', ctx, value);
    }
  }
  // v-model이 아니면 그냥 value 할당
  ctx.el.value = value;
}

/**
 * petite-vue용 format-number 커스텀 디렉티브 등록
 */
export function petiteDirectiveFormatNumber(petiteVueApp) {
  return petiteVueApp.directive('format-number', (ctx) => {
    let snapValue = '';

    // modifiers가 없으면 ["compact"]
    let modifiers = Object.keys(ctx.modifiers || {});
    if (modifiers.length === 0) {
      modifiers.push("compact");
    }

    // focus 이벤트: 현재 값 스냅
    const handleFocus = () => {
      snapValue = getValueOfPetiteNode(ctx);
      const currentElValue = getValueOfPetiteNode(ctx);
      if (modifiers.includes('currency')) {
        // 필드의 값이 0일때 0을 제거하고 시작
        if (currentElValue === '0') {
          setValueOfPetiteNode(ctx, '');
        }
      }
    };

    // blur 이벤트 처리
    const handleBlur = () => {
      try {
        let elValue = getValueOfPetiteNode(ctx);
        if (typeof elValue !== 'string') elValue = String(elValue);
        if (elValue.trim() === '') {
          // snapValue 가 0이 였고 현재 값이 비어있으면(trim) 0으로 복구
          if (String(snapValue).trim() === '0') {
            setValueOfPetiteNode(ctx, snapValue);
          } else {
            setValueOfPetiteNode(ctx, '');
          }
          return;
        }
        if (modifiers.includes('phone')) {
          // 포맷 불가 번호(지역번호 등)는 입력값을 지우지 않고 원본 유지 — 유효성 판정은 서버(TextEasy.korea_phone_number?) 책임 #164
          elValue = formatInKoreaPhoneNumber(elValue) || elValue;
        }
        if (modifiers.includes('compact')) {
          elValue = elValue.replace(/[^0-9]/g, '').replace(/(\..*)\./g, '$1');
        }
        if (modifiers.includes('currency')) {
          elValue = compactCurrencyNumber(elValue) || "";
        }
        setValueOfPetiteNode(ctx, elValue);
      } catch (e) {
        setValueOfPetiteNode(ctx, snapValue);
        console.error(e);
      }
    };

    ctx.el.addEventListener('focus', handleFocus);
    ctx.el.addEventListener('blur', handleBlur);

    // clean up: 언마운트 시 이벤트 제거
    return () => {
      ctx.el.removeEventListener('focus', handleFocus);
      ctx.el.removeEventListener('blur', handleBlur);
    };
  });
}

export function petiteDirectiveCustomValidations(petiteVueApp) {
  return petiteVueApp.directive('custom-validations', (ctx) => {
    // expression을 평가하여 validityTargets 배열을 가져옵니다.
    const contextedExpression = `(()=>{
      function required(_1) {
        function generateRequired(message="필수 입력 항목입니다") {
          return ({value}) => value === null ? message : String(value).trim() === '' ? message : ''
        }
        if(arguments.length === 0) return generateRequired()
        if(typeof _1 === "string") return generateRequired(_1)
        return generateRequired()(_1)
      }
      function minlength(length) {
        return ({ value }) => String(value).length < length ? ('최소 ' + length + '자 이상 입력해야 합니다') : ''
      }
      function maxlength(length) {
        return ({ value }) => String(value).length > length ? ('최대 ' + length + '자 이하로 입력해야 합니다') : ''
      }
      function min(min) {
        return ({ value }) => Number(value) < min ? '최소 ' + min + ' 이상 입력해야 합니다' : ''
      }
      function max(max) {
        return ({ value }) => Number(value) > max ? ('최대 ' + max + ' 이하로 입력해야 합니다') : ''
      }
      function pattern(pattern) {
        return ({ value }) => !new RegExp(pattern).test(value) ? '올바른 형식이 아닙니다' : ''
      }
      function equals(values){
        return ({ value }) => {
          Array.from(values).some(v => v === value);
        }
      }
      return ${ctx.exp}
    })()`;

    // petite-vue에서는 ctx.get으로 expression 평가
    let customValidations = null;
    function getCustomValidations() {
      try {
        // new Function을 통한 평가 (보안 주의)
        // ctx.scope, ctx.ctx 등에서 변수 접근 필요시 수정
        // eslint-disable-next-line no-new-func
        return asArray(Function('scope', `with(scope){return ${contextedExpression}}`)(ctx.scope || {}));
      } catch (e) {
        console.warn('custom-validations expression error', e);
        return [];
      }
    }

    function getValue() {
      if (ctx.exp) {
        try {
          return String(ctx.get());
        } catch {
          // fallback
        }
      }
      return ctx.el.value;
    }

    function validateSingle(validationFunction) {
      const value = getValue();
      const name = ctx.el.name;
      const type = ctx.el.type;
      const result = validationFunction({ value, name, type }, ctx.el);
      if (typeof result === 'string') return result;
      if (result === true) return '';
      if (result === false) return '올바른 입력이 아닙니다';
      if (result === null) return '';
      console.warn("custom-validations 유효하지 않은 값", result, validationFunction);
      return String(result);
    }

    function validateAll(validations) {
      let destCustomValidity = '';
      validations.some((validationFunction) => {
        const result = validateSingle(validationFunction);
        if (result.length) {
          destCustomValidity = result;
          return true;
        }
      });
      return destCustomValidity;
    }

    function updateValidity() {
      if (!customValidations) return;
      const message = validateAll(customValidations);
      ctx.el.setCustomValidity(message || '');
      dispatchElement(ctx.el, 'customvalidation:updated');
    }

    // 이벤트 바인딩
    const handleUpdate = () => updateValidity();
    ctx.el.addEventListener('input', handleUpdate);
    ctx.el.addEventListener('blur', handleUpdate);
    ctx.el.addEventListener('touch', handleUpdate);

    // 최초 및 reactive 업데이트
    function effect() {
      customValidations = getCustomValidations().filter(fn => typeof fn === 'function');
      updateValidity();
    }
    // petite-vue에서는 effect를 직접 호출하거나, v-scope 내에서 watch로 연결
    effect();

    // clean up
    return () => {
      ctx.el.removeEventListener('input', handleUpdate);
      ctx.el.removeEventListener('blur', handleUpdate);
      ctx.el.removeEventListener('touch', handleUpdate);
    };
  });
}

/*
v-model에 의존하지 않는 input요소를 역으로 바인딩 하기 위한 directive
최초 directive에 바인딩 될때와 input 이벤트 발생시 작동함
<div v-scope="{
  scope: {
    starting_time: null,
    schedule_measured_time_is: null
  }
}">
  <input type="text" v-input-effect="scope.starting_time = $value">
  <select v-input-effect="scope.schedule_measured_time_is = $value">
  </select>
</div>

*/

export function petiteDirectiveInputEffect(petiteVueApp) {
  // input 요소의 현재 값을 가져오는 함수
  function getComputedValueOfContext(ctx) {
    const el = ctx.el;
    if (el.type === 'checkbox') {
      return el.checked;
    } else if (el.type === 'radio') {
      return el.checked ? el.value : null;
    } else if (el.tagName === 'SELECT' && el.multiple) {
      return Array.from(el.selectedOptions).map(option => option.value);
    } else {
      return el.value;
    }
  }

  // 컨택스트에서 expression을 실행 컨텍스트에서 $el, $value, $entry 를 지원
  function executeInputEffect(ctx, computedValue) {
    const expression = ctx.exp
    try {
      // petite-vue의 ctx.get을 활용하여 expression을 평가
      // ctx.get() 메서드가 scope 체인을 자동으로 처리함
      const wrappedExpression = `
          (function($el, $value, $detail) {
            ${expression}
          })
        `;
      const currentName = ctx.el.name
      const currentDetail = {
        get entry() { return [currentName, computedValue] },
      }
      const expressionFunction = ctx.get(wrappedExpression);
      expressionFunction(ctx.el, computedValue, currentDetail);
    } catch (e) {
      console.warn('v-input-effect expression 실행 오류:', e, expression, 'value:', computedValue);
      console.warn('Available ctx:', ctx);
      console.warn('ctx.scope:', ctx.scope);
    }
  }

  // 컨택스트에서 expression을 실행 컨텍스트에서 $el, $value, $dirty, $detail, $entry, $changes 를 지원
  // $changes: [초기값string, 현재값string] (변경됨) 또는 null (변경 없음)
  function executeDirtyEffect(ctx, isInitial, computedValue, commitedRawValue) {
    const expression = ctx.exp;
    try {
      // petite-vue의 ctx.get을 활용하여 expression을 평가
      // ctx.get() 메서드가 scope 체인을 자동으로 처리함
      const wrappedExpression = `
          (function($el, $initial, $value, $detail, $dirty, $changes) {
            ${expression}
          })
        `;
      const currentName = ctx.el.name
      const currentRawValue = ctx.el.value
      // $dirty: 초기값과 현재값 비교 (changed? 와 유사 )
      const currentDirty = commitedRawValue !== currentRawValue
      // $changes: 변경됨이면 [초기값, 현재값], 변경 없으면 null
      const currentChanges = currentDirty ? [commitedRawValue, currentRawValue] : null
      const currentDetail = {
        get entry() { return [currentName, computedValue] },
        get beforeRaw() { return commitedRawValue },
        get afterRaw() { return currentRawValue },
      }
      const expressionFunction = ctx.get(wrappedExpression);
      expressionFunction(ctx.el, isInitial, computedValue, currentDetail, currentDirty, currentChanges);
    } catch (e) {
      console.warn('v-dirty-effect expression 실행 오류:', e, expression, 'value:', computedValue);
      console.warn('Available ctx:', ctx);
      console.warn('ctx.scope:', ctx.scope);
    }
  }

  function executeValidEffect(ctx, isInitial, computedValue) {
    const expression = ctx.exp
    try {
      // petite-vue의 ctx.get을 활용하여 expression을 평가
      // ctx.get() 메서드가 scope 체인을 자동으로 처리함
      const wrappedExpression = `
          (function($el, $initial, $value, $message, $messageIfEq, $detail) {
            ${expression}
          })
        `;
      const currentName = ctx.el.name
      const currentInvalidMessage = ctx.el.validationMessage
      const eqProc = (message, eqValue) => {
        if (typeof message === "function") {
          message = message();
        }
        if (typeof message !== "string") {
          console.warn("v-invalid-effect $messageIfEq expected string or ()=>string", { message });
          return null;
        }
        if (typeof eqValue === "function") {
          try {
            eqValue = eqValue();
          } catch {
            console.warn("v-invalid-effect $messageIfEq eq function error", { eq });
            return null;
          }
        }
        if (eqValue === computedValue) {
          return message;
        } else {
          return null;
        }
      }
      const currentDetail = {
        get entry() { return [currentName, computedValue] },
      }
      const expressionFunction = ctx.get(wrappedExpression);
      expressionFunction(ctx.el, isInitial, computedValue, currentInvalidMessage, eqProc, currentDetail);
    } catch (e) {
      console.warn('v-input-effect expression 실행 오류:', e, expression, 'value:', computedValue);
      console.warn('Available ctx:', ctx);
      console.warn('ctx.scope:', ctx.scope);
    }
  }


  const inputEffectApp = petiteVueApp.directive('input-effect', (ctx) => {
    const expression = ctx.exp;
    if (!expression) {
      console.warn('v-input-effect: expression이 필요합니다');
      return;
    }

    // 초기값 설정
    const initialComputedValue = getComputedValueOfContext(ctx);
    executeInputEffect(ctx, initialComputedValue);

    // input 이벤트 핸들러
    const handleInputOrChange = (event) => {
      //console.log("event.type", event.type, event.target.value)
      const computedValue = getComputedValueOfContext(ctx);
      executeInputEffect(ctx, computedValue);
    };

    // 이벤트 리스너 등록
    ctx.el.addEventListener('change', handleInputOrChange);
    ctx.el.addEventListener('input', handleInputOrChange);
    ctx.el.addEventListener('input:effect', handleInputOrChange);

    // cleanup 함수
    return () => {
      ctx.el.removeEventListener('change', handleInputOrChange);
      ctx.el.removeEventListener('input', handleInputOrChange);
      ctx.el.addEventListener('input:effect', handleInputOrChange);
    };
  });

  const dirtyEfffectApp = inputEffectApp.directive('dirty-effect', (ctx) => {
    const expression = ctx.exp;
    if (!expression) {
      console.warn('v-dirty-effect: expression이 필요합니다');
      return;
    }

    // 초기값 설정 (Rails 템플릿에서 value가 입력되어 있음)
    let commitedRawValue = ctx.el.value

    const initialComputedValue = getComputedValueOfContext(ctx);
    executeDirtyEffect(ctx, true, initialComputedValue, commitedRawValue);

    // input 이벤트 핸들러
    const handleInputOrChange = () => {
      const computedValue = getComputedValueOfContext(ctx);
      executeDirtyEffect(ctx, false, computedValue, commitedRawValue);
    };

    // input:dirty:reset, input:change:reset 이벤트 핸들러 - 현재 값을 초기값으로 설정하고 dirty 리셋
    const handleDirtyReset = () => {
      commitedRawValue = ctx.el.value
      const computedValue = getComputedValueOfContext(ctx);
      // 리셋 후에는 initial처럼 취급 (dirty = false, changes = null)
      executeDirtyEffect(ctx, true, computedValue, commitedRawValue);
    };

    // 이벤트 리스너 등록
    ctx.el.addEventListener('change', handleInputOrChange);
    ctx.el.addEventListener('input', handleInputOrChange);
    ctx.el.addEventListener('input:effect', handleInputOrChange);
    ctx.el.addEventListener('input:dirty:reset', handleDirtyReset);
    ctx.el.addEventListener('input:change:reset', handleDirtyReset);

    // cleanup 함수
    return () => {
      ctx.el.removeEventListener('change', handleInputOrChange);
      ctx.el.removeEventListener('input', handleInputOrChange);
      ctx.el.removeEventListener('input:effect', handleInputOrChange);
      ctx.el.removeEventListener('input:dirty:reset', handleDirtyReset);
      ctx.el.removeEventListener('input:change:reset', handleDirtyReset);
    };
  });

  // v-change-effect: change 이벤트에만 반응 (input 이벤트에는 반응 안함)
  // $dirty, $changes 지원
  // input:effect, input:dirty:reset, input:change:reset 이벤트 대응
  const changeEffectApp = dirtyEfffectApp.directive('change-effect', (ctx) => {
    const expression = ctx.exp;
    if (!expression) {
      console.warn('v-change-effect: expression이 필요합니다');
      return;
    }

    // 초기값 설정 (Rails 템플릿에서 value가 입력되어 있음)
    let commitedRawValue = ctx.el.value

    const initialComputedValue = getComputedValueOfContext(ctx);
    executeDirtyEffect(ctx, true, initialComputedValue, commitedRawValue);

    // change 이벤트 핸들러 (input 이벤트에는 반응하지 않음)
    const handleChange = () => {
      const computedValue = getComputedValueOfContext(ctx);
      executeDirtyEffect(ctx, false, computedValue, commitedRawValue);
    };

    // input:dirty:reset, input:change:reset 이벤트 핸들러 - 현재 값을 초기값으로 설정하고 리셋
    const handleReset = () => {
      commitedRawValue = ctx.el.value
      const computedValue = getComputedValueOfContext(ctx);
      // 리셋 후에는 initial처럼 취급 (dirty = false, changes = null)
      executeDirtyEffect(ctx, true, computedValue, commitedRawValue);
    };

    // 이벤트 리스너 등록 (input 이벤트 제외)
    ctx.el.addEventListener('change', handleChange);
    ctx.el.addEventListener('input:effect', handleChange);
    ctx.el.addEventListener('input:dirty:reset', handleReset);
    ctx.el.addEventListener('input:change:reset', handleReset);

    // cleanup 함수
    return () => {
      ctx.el.removeEventListener('change', handleChange);
      ctx.el.removeEventListener('input:effect', handleChange);
      ctx.el.removeEventListener('input:dirty:reset', handleReset);
      ctx.el.removeEventListener('input:change:reset', handleReset);
    };
  });

  const validEfffectApp = changeEffectApp.directive('valid-effect', (ctx) => {
    const expression = ctx.exp;
    if (!expression) {
      console.warn('v-valid-effect: expression이 필요합니다');
      return;
    }

    const initialComputedValue = getComputedValueOfContext(ctx);
    executeValidEffect(ctx, true, initialComputedValue);

    // input 이벤트 핸들러
    const handleInputOrChange = () => {
      const computedValue = getComputedValueOfContext(ctx);
      executeValidEffect(ctx, false, computedValue);
    };

    // 이벤트 리스너 등록
    ctx.el.addEventListener('change', handleInputOrChange);
    ctx.el.addEventListener('input', handleInputOrChange);
    ctx.el.addEventListener('input:effect', handleInputOrChange);

    // cleanup 함수
    return () => {
      ctx.el.removeEventListener('change', handleInputOrChange);
      ctx.el.removeEventListener('input', handleInputOrChange);
      ctx.el.addEventListener('input:effect', handleInputOrChange);
    };
  });

  return validEfffectApp
}
